import React from 'react';

/**
 * 🟢 Active            — recent or unfinished work
 * 🟡 Awaiting Payment  — has remaining balance
 * 🔵 Completed         — fully paid, > 30d old
 * ⚫ Archived          — moved to cold storage
 * 🔴 Problem / Debt    — refund pending or rejected
 *
 * Computed by `_compute_op_status` on the backend; this component just
 * renders the matching pill. Pass either `status` (one of the above)
 * or compute it inline from an operation prop.
 */
const STATUS_CONFIG = {
  active: {
    label: 'نشطة',
    icon: '🟢',
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  awaiting_payment: {
    label: 'بانتظار الدفع',
    icon: '🟡',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  completed: {
    label: 'مكتملة',
    icon: '🔵',
    className: 'bg-brand-100 text-brand-800 border-brand-200',
  },
  archived: {
    label: 'مؤرشفة',
    icon: '⚫',
    className: 'bg-gray-200 text-gray-700 border-gray-300',
  },
  problem: {
    label: 'مشكلة',
    icon: '🔴',
    className: 'bg-rose-100 text-rose-800 border-rose-200',
  },
};

export const computeOpStatus = (op) => {
  if (!op) return 'active';
  if (op.lifecycle_status === 'archived') return 'archived';
  if ((op.pending_refund_credit || 0) > 0) return 'problem';
  if (op.status === 'مرفوض') return 'problem';
  const final = Number(op.final_price || 0);
  const paid = Number(op.total_paid || 0);
  if (final - paid > 0.01) return 'awaiting_payment';
  // Fully paid — decide by age
  const created = op.created_at || op.date;
  if (created) {
    const ageMs = Date.now() - new Date(created).getTime();
    if (ageMs > 30 * 24 * 60 * 60 * 1000) return 'completed';
  }
  return 'active';
};

export const LifecycleBadge = ({ status, operation, size = 'sm', className = '' }) => {
  const finalStatus = status || computeOpStatus(operation);
  const cfg = STATUS_CONFIG[finalStatus] || STATUS_CONFIG.active;
  const sizeCls = size === 'xs'
    ? 'text-[10px] px-1.5 py-0.5'
    : 'text-xs px-2 py-0.5';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-semibold ${cfg.className} ${sizeCls} ${className}`}
      data-testid={`lifecycle-badge-${finalStatus}`}
      title={cfg.label}
    >
      <span>{cfg.icon}</span>
      <span>{cfg.label}</span>
    </span>
  );
};

export default LifecycleBadge;
